import { Component, effect, inject, OnDestroy, Signal, signal, viewChild, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { ListComponent } from '../../components/list/list.component';
import { MatIcon } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import TYPES from '../../constants/types';
import { AUTH_SERVICE, AuthService } from '../../services/auth.service';
import { TranslatePipe } from '@ngx-translate/core';
import { StreakComponent } from '../../components/streak/streak.component';
import { MoodProgressComponent } from '../../components/mood-progress/mood-progress.component';
import { Psychologist } from '../../models/psychologists';
import { LogService } from '../../services/log.service';
import { Log } from '../../models/log';
import { PsychologistService } from '../../services/psychologist.service';
import { UserPsychologistService } from '../../services/user-psychologist.service';
import { UserPsychologist } from '../../models/user-psychologist';
import { ConfirmDialogComponent } from '../../components/confirm-dialog/confirm-dialog.component';
import { Dialog } from '@angular/cdk/dialog';
import { Router } from '@angular/router';

@Component({
  selector: 'app-historical',
  imports: [
    ListComponent,
    StreakComponent,
    MoodProgressComponent,
    MatIcon,
    MatMenuModule,
    TranslatePipe
  ],
  templateUrl: './historical.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './historical.component.scss'
})
export class HistoricalComponent implements OnDestroy {
  yourMindLogIsActive:boolean;
  sharedWithYouIsActive:boolean;
  viewType: string;  
  isMenuOpened: boolean = false;

  authService: AuthService = inject(AUTH_SERVICE);
  logService: LogService<Log> = inject(LogService);
  psychologistService: PsychologistService<Psychologist> = inject(PsychologistService);
  userPsychologistService: UserPsychologistService<UserPsychologist> = inject(UserPsychologistService);

  dialog: Dialog = inject(Dialog);

  PROFILES: any;
  DATAVIEW: any;
  PLANS: any;

  observer!: IntersectionObserver;

  listComponent:Signal<ListComponent | undefined> = viewChild(ListComponent);
  
  list: WritableSignal<Log[]> = signal([]);
  listShared: WritableSignal<any[]> = signal([]);

  psychologist: WritableSignal<Psychologist | undefined> = signal(undefined);
  userFiltered: string = '';

  logsCurrentPage: number = 0;
  hasMoreLogs: boolean = true;
  logsSharedCurrentPage: number = 0;
  hasMoreSharedLogs: boolean = true;

  loading: WritableSignal<boolean> = signal(false);
  loadingShared: WritableSignal<boolean> = signal(false);
  hasReachedStarterLimitForLogShared: WritableSignal<boolean> = signal(false);
  hasReachedStarterLimitForUsersSharing: WritableSignal<boolean> = signal(false);

  constructor(private router: Router){
    effect(
      () => {
        if(this.listComponent()?.logsAnchor()){
          this.observer = new IntersectionObserver(([entry]) => {            
            if (entry.isIntersecting && this.hasMoreLogs) {
              this.loadLogsFromUser();
            }
          });
          this.observer.observe(this.listComponent()?.logsAnchor()?.nativeElement);
        }
        if(this.listComponent()?.logsSharedAnchor()){
          this.observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting && this.hasMoreSharedLogs) {
              if(this.psychologist() && this.userFiltered != ''){
                const psychologistId = this.psychologist()?.id;
                if(psychologistId){
                  this.loadLogsShared(psychologistId, this.userFiltered);
                }
              }
            }
          });
          this.observer.observe(this.listComponent()?.logsSharedAnchor()?.nativeElement);
        }  
      }
    );

    this.logService.canAccessMindLogRegister.set(false);

    this.PROFILES = TYPES.DATABASE.PROFILES;
    this.DATAVIEW = TYPES.UI.DATAVIEW;
    this.PLANS = TYPES.DATABASE.PLANS;
    this.viewType = localStorage.getItem('VIEWTYPE') ?? 'list';

    this.yourMindLogIsActive = false;
    this.sharedWithYouIsActive = true;
    
    this.selectTab();

    this.loadLogsFromUser();

  }

  ngOnDestroy() {
    this.observer?.disconnect();
  }

  async selectTab(){

    if(!this.authService.user()){
      await this.authService.getUser();
    }

    if(this.authService.user()?.profile === this.PROFILES.PSYCHOLOGIST.key){

      const userId = this.authService.user()?.id;
      if(userId){
        this.psychologistService.getPsychologistByUserId(userId).then(
          (data) => {
            this.psychologist.set(data ?? undefined);
            if(this.psychologist()){
              if(this.psychologist()?.subscription_plan === this.PLANS.STARTER.code){
                this.hasReachLimit();
              }
            }
          }
        );
      }
      this.yourMindLogIsActive = false;
      this.sharedWithYouIsActive = true;

    }else{
      this.yourMindLogIsActive = true;
      this.sharedWithYouIsActive = false;
    }

  }

  loadLogsFromUser(){
    if(this.logsCurrentPage === 0){
      this.loading.set(true);
    }
    if(this.hasMoreLogs || this.logsCurrentPage === 0){
      this.logService.getAllLogsFromUserPaginated(
        this.authService.user()?.id || '', 
        this.logsCurrentPage,
        TYPES.DATABASE.PAGINATION.LOGS_PAGE_SIZE
      ).then(
        (result) => {
          this.list.update((current) => {
            return current.concat(result.logs);
          });
          
          this.hasMoreLogs = result.hasMore;
          this.logsCurrentPage += 1;
          this.loading.set(false);
        }
      );
    }
  }

  loadLogsShared(psychologistId:string, userId:string){
    if(this.psychologist()?.subscription_plan === this.PLANS.STARTER.code){
      if(this.logsSharedCurrentPage === 0){
        this.loadingShared.set(true);
      }
      this.logService.getLogsSharedWithUserForStarterPlan(
        psychologistId, 
        userId,
      ).then(
        (result) => {
          this.listShared.update((current) => {
            return current.concat(result.reverse());
          });
          this.hasMoreSharedLogs = false;
          this.logsSharedCurrentPage += 1;
          this.loadingShared.set(false);
        }
      );
    }else{
      if(this.logsSharedCurrentPage === 0){
        this.loadingShared.set(true);
      }
      if(this.hasMoreSharedLogs || this.logsSharedCurrentPage === 0){
        this.logService.getAllLogsSharedWithPsychologistByUserPaginated(
          psychologistId, 
          userId,
          this.logsSharedCurrentPage,
          TYPES.DATABASE.PAGINATION.LOGS_PAGE_SIZE
        ).then(
          (result) => {
            this.listShared.update((current) => {
              return current.concat(result.logs);
            });
            this.hasMoreSharedLogs = result.hasMore;
            this.logsSharedCurrentPage += 1;
            this.loadingShared.set(false);
          }
        );
      }
    }    
  }

  hasReachLimit(){
    const psychologisId = this.psychologist()?.id
    if(psychologisId){
      this.logService.hasReachedLimit(psychologisId).then(
        (data) => {
          this.hasReachedStarterLimitForLogShared.set(data);
          if(this.hasReachedStarterLimitForLogShared()){
            this.openLimitReachedDialog();
          }else{
            this.userPsychologistService.hasReachedLimit(psychologisId).then(
              (data) => {
                this.hasReachedStarterLimitForUsersSharing.set(data);
                if(this.hasReachedStarterLimitForUsersSharing()){
                  this.openLimitReachedDialog();
                }
              }
            );
          }          
        }
      );      
    }    
  }

  openLimitReachedDialog(){
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'starter-limit-reached-dialog.title',
        message: 'starter-limit-reached-dialog.message',
        question: 'starter-limit-reached-dialog.question',
        image: '/imgs/tired.png'
      },
    });

    dialogRef.closed.subscribe(async(result:any)  => {
      if(result?.action === 'continue'){
        this.router.navigate(['/choose-a-plan']);
      }
    });
  }

  activateTab(tab:string){
    if(tab === 'mindLog'){
      this.yourMindLogIsActive = true;
      this.sharedWithYouIsActive = false;
    }else if(tab === 'shared'){
      this.yourMindLogIsActive = false;
      this.sharedWithYouIsActive = true;
    }
  }

  toggleMenuOpen(){
    if ('vibrate' in navigator) {
      navigator.vibrate(100);
    }

    this.isMenuOpened = !this.isMenuOpened;
  }

  changeView(type:string){
    if ('vibrate' in navigator) {
      navigator.vibrate(100);
    }

    switch(type){
      case 'list':
        this.viewType = this.DATAVIEW.LIST;        
        break;
      case 'streak':
        this.viewType = this.DATAVIEW.STREAK;
        break;
      case 'progress':
        this.viewType = this.DATAVIEW.PROGRESS;
        break;
      default:
        this.viewType = this.DATAVIEW.LIST;
        break;
    }

    localStorage.setItem('VIEWTYPE', this.viewType);

  }

  filterByUser(userId:any){
    this.userFiltered = userId;
    if(this.psychologist()){
      this.hasMoreSharedLogs = true;
      this.logsSharedCurrentPage = 0;
      this.listShared.set([]);

      const psychologistId = this.psychologist()?.id;
      if(psychologistId){
        this.loadLogsShared(psychologistId, userId);
      }
    }
  }

}
